import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InlineStatus from "@/components/InlineStatus";
import { ENTREGA } from "@/lib/statuses";
import { toast } from "sonner";
import { MapPinned, Phone, MapPin, Search, ExternalLink, Route, Download } from "lucide-react";
import { isArchived, historyCounts } from "@/lib/archive";
import HistoryCounter from "@/components/HistoryCounter";
import EditableNote from "@/components/EditableNote";
import { sortManual, useManualOrder, MoveHandle, PRIO_ROW } from "@/lib/ordenManual";
import { PrioBadge } from "@/components/PrioridadToggle";

export default function Entrega() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("activos");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState({}); // { orderId: true }

  const load = async () => {
    try {
      const { data } = await api.get("/orders", { params: { tipo: "ciudad" } });
      const active = data.filter((o) => o.estado !== "cancelado");
      setOrders(active);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const reprogramar = async (o) => {
    const desdeIni = o.fecha_entrega || "";
    const hastaIni = o.fecha_entrega_hasta || o.fecha_entrega || "";
    const nuevaDesde = window.prompt(`Reprogramar entrega de ${o.numero}\nFecha DESDE actual: ${desdeIni || "sin fecha"}\n\nNueva fecha DESDE (YYYY-MM-DD):`, desdeIni);
    if (!nuevaDesde) return;
    const nuevaHasta = window.prompt(`Fecha HASTA actual: ${hastaIni || "sin fecha"}\n\nNueva fecha HASTA (YYYY-MM-DD):`, hastaIni || nuevaDesde);
    if (!nuevaHasta) return;
    if (nuevaHasta < nuevaDesde) { toast.error("Hasta debe ser ≥ Desde"); return; }
    try {
      await api.patch(`/orders/${o.id}/fecha-entrega`, { fecha: nuevaDesde, fecha_hasta: nuevaHasta });
      toast.success("Fecha reprogramada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const historicos = orders.filter((o) => o.estado_entrega === "entregado");
  const historialActivos = historicos.filter((o) => !isArchived(o, "estado_entrega_at"));
  const historialArchivados = historicos.filter((o) => isArchived(o, "estado_entrega_at"));

  const byView = view === "historial"
    ? (showArchived ? historicos : historialActivos)
    : orders.filter((o) => (o.estado_entrega || "pendiente") !== "entregado");
  const s = q.trim().toLowerCase();
  const filtered = s
    ? byView.filter((o) =>
        (o.numero || "").toLowerCase().includes(s) ||
        (o.cliente_nombre || "").toLowerCase().includes(s) ||
        (o.direccion || "").toLowerCase().includes(s) ||
        (o.creado_por_nombre || "").toLowerCase().includes(s) ||
        (o.items || []).some((it) => (it.product_name || "").toLowerCase().includes(s))
      )
    : byView;
  const ordered = sortManual(filtered, "entrega_ciudad");
  const { move, handleProps, rowProps } = useManualOrder("entrega_ciudad", ordered, setOrders);
  const canMove = !s;

  const totales = {
    count: filtered.length,
    aCobrar: filtered.reduce((n, o) => n + (o.a_cobrar != null ? o.a_cobrar : (o.total - (o.anticipo || 0))), 0),
    cobrado: filtered.reduce((n, o) => n + (o.monto_facturado_acum || 0), 0),
    entregados: filtered.filter((o) => o.estado_entrega === "entregado").length,
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedInView = filtered.filter((o) => selected[o.id]);
  const allInViewSelected = filtered.length > 0 && filtered.every((o) => selected[o.id]);

  const toggleAll = () => {
    if (allInViewSelected) {
      const copy = { ...selected };
      filtered.forEach((o) => { delete copy[o.id]; });
      setSelected(copy);
    } else {
      const copy = { ...selected };
      filtered.forEach((o) => { copy[o.id] = true; });
      setSelected(copy);
    }
  };
  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const exportPlanRuta = () => {
    if (selectedInView.length === 0) {
      toast.error("Selecciona al menos un pedido para el Plan de Ruta");
      return;
    }
    // Build CSV client-side (respects user-selected rows) — includes columns shown in the table.
    const rows = [
      ["Pedido", "Vendedor", "Cliente", "Teléfono", "Teléfono alt", "Dirección", "Productos",
       "Fecha entrega", "Precio de envío", "Total", "Anticipo", "A cobrar", "Estado"],
      ...selectedInView.map((o) => [
        o.numero,
        o.creado_por_nombre || "",
        o.cliente_nombre || "",
        o.cliente_telefono || "",
        o.cliente_telefono2 || "",
        (o.direccion || "").replace(/\r?\n/g, " "),
        (o.items || []).map((it) => `${it.cantidad}× ${it.product_name}`).join(" | "),
        o.fecha_entrega || "",
        (o.envio_precio ?? 0),
        (o.total ?? 0),
        (o.anticipo ?? 0),
        (o.a_cobrar != null ? o.a_cobrar : (o.total - (o.anticipo || 0))),
        o.estado_entrega || "pendiente",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `plan_de_ruta_${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success(`Plan de Ruta exportado (${selectedInView.length} pedidos)`);
  };

  return (
    <div className="space-y-6" data-testid="entrega-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Distribución</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <MapPinned className="w-8 h-8 text-[#3B82F6]" /> Entregas Ciudad
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Reporte del piloto — lista completa de pedidos de ciudad con anticipo, a cobrar y pago recibido.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={exportPlanRuta}
            className="bg-[#002FA7] hover:bg-[#00227A] gap-2 disabled:opacity-40"
            disabled={selectedInView.length === 0}
            data-testid="plan-ruta-btn"
          >
            <Route className="w-4 h-4" />
            Plan de Ruta
            <span className="text-[10px] bg-white/20 rounded-full px-1.5 py-0.5">{selectedInView.length}</span>
          </Button>
        </div>
      </header>

      {/* View tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 -mb-2">
        {[
          { v: "activos", label: "Activos", count: orders.filter((o) => (o.estado_entrega || "pendiente") !== "entregado").length },
          { v: "historial", label: "Historial (entregados)", count: historialActivos.length },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setView(t.v)}
            data-testid={`ent-view-${t.v}`}
            className={`px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              view === t.v ? "border-[#3B82F6] text-[#1D4ED8] font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
            <span className="ml-1 text-[10px] bg-zinc-100 rounded-full px-1.5 py-0.5">{t.count}</span>
          </button>
        ))}
        {view === "historial" && historialArchivados.length > 0 && (
          <label className="ml-auto text-xs text-zinc-600 flex items-center gap-2 cursor-pointer" data-testid="ent-archive-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Incluir archivados <span className="text-zinc-400">({historialArchivados.length} de +1 día)</span>
          </label>
        )}
      </div>

      {/* KPI bar */}
      {view === "historial" && (
        <HistoryCounter counts={historyCounts(historicos, "estado_entrega_at")} label="Entregados" />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos</div>
          <div className="font-manrope font-black text-3xl mt-1">{totales.count}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Por cobrar</div>
          <div className="font-manrope font-black text-3xl mt-1 text-orange-700">{formatMoney(totales.aCobrar)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cobrado</div>
          <div className="font-manrope font-black text-3xl mt-1 text-emerald-700">{formatMoney(totales.cobrado)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Entregados</div>
          <div className="font-manrope font-black text-3xl mt-1">{totales.entregados} / {totales.count}</div>
        </Card>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por pedido, cliente, vendedor, producto o dirección..." className="pl-9" data-testid="ent-search" />
        </div>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => setSelected({})}
            className="text-xs text-zinc-600 hover:text-zinc-900 underline"
            data-testid="ent-clear-selection"
          >
            Limpiar selección ({selectedIds.length})
          </button>
        )}
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allInViewSelected}
                    onChange={toggleAll}
                    aria-label="Seleccionar todo"
                    data-testid="ent-select-all"
                  />
                </th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Vendedor</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Notas</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fecha entrega</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Productos</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">Precio envío</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">Total</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">Anticipo</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">A cobrar</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr><td colSpan={13} className="text-center text-zinc-400 py-12">
                  <MapPinned className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                  Sin pedidos de ciudad.
                </td></tr>
              )}
              {ordered.map((o, i) => {
                const aCobrar = o.a_cobrar != null ? o.a_cobrar : (o.total - (o.anticipo || 0));
                return (
                  <tr key={o.id} {...rowProps(i)} className={`align-top ${o.prioridad_entrega ? PRIO_ROW : `hover:bg-zinc-50 ${selected[o.id] ? "bg-blue-50/60" : ""}`}`} data-testid={`ent-row-${o.numero}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-start gap-1">
                      <input
                        type="checkbox"
                        checked={!!selected[o.id]}
                        onChange={() => toggle(o.id)}
                        aria-label={`Seleccionar ${o.numero}`}
                        data-testid={`ent-select-${o.numero}`}
                      />
                      {canMove && <MoveHandle i={i} n={ordered.length} move={move} handleProps={handleProps} testid={`ent-move-${o.numero}`} />}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="font-mono text-xs font-semibold">{o.numero}</div>
                      <PrioBadge show={o.prioridad_entrega} />
                      <div className="text-[10px] text-zinc-500 mt-0.5">{formatDate(o.fecha)}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-zinc-700 whitespace-nowrap">
                      {o.creado_por_nombre || <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-3 py-3 max-w-[220px] min-w-[180px]">
                      <EditableNote
                        orderId={o.id}
                        modulo="notas_ent" initial={o.notas_ent ?? o.notas_hoja ?? ""}
                        onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_ent: v } : x))}
                      />
                      {o.notas && (
                        <div className="mt-1 text-[10px] text-zinc-500 italic border-l-2 border-zinc-300 pl-2">
                          {o.notas}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {o.fecha_entrega ? (
                        <span className="text-sm font-semibold text-orange-700">
                          {formatDate(o.fecha_entrega)}
                          {o.fecha_entrega_hasta && o.fecha_entrega_hasta !== o.fecha_entrega && (
                            <span className="text-zinc-500"> → {formatDate(o.fecha_entrega_hasta)}</span>
                          )}
                        </span>
                      ) : <span className="text-zinc-300 text-xs">—</span>}
                      <div className="text-[10px] text-zinc-500 mt-0.5 capitalize">{o.envio_nombre || "—"}</div>
                      <button
                        onClick={() => reprogramar(o)}
                        className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold mt-1 hover:underline"
                        data-testid={`reprog-${o.numero}`}
                      >
                        Cambio de fecha
                      </button>
                    </td>
                    <td className="px-3 py-3 min-w-[160px]">
                      <div className="font-semibold">{o.cliente_nombre}</div>
                      {o.cliente_telefono && (
                        <a href={`tel:${o.cliente_telefono}`} className="text-[11px] text-blue-700 hover:underline flex items-center gap-1">
                          <Phone className="w-3 h-3" />{o.cliente_telefono}
                        </a>
                      )}
                      {o.cliente_telefono2 && (
                        <a href={`tel:${o.cliente_telefono2}`} className="text-[11px] text-blue-500 hover:underline flex items-center gap-1">
                          <Phone className="w-3 h-3" />alt {o.cliente_telefono2}
                        </a>
                      )}
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{o.direccion}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 min-w-[240px] max-w-md">
                      <ul className="space-y-0.5">
                        {(o.items || []).map((it, i) => (
                          <li key={i} className="text-xs flex items-start gap-2">
                            <span className="inline-block min-w-[24px] text-right font-mono font-bold text-zinc-900">{it.cantidad}×</span>
                            <span className="flex-1">
                              <span className="font-medium">{it.product_name}</span>
                              {(it.color || it.tamano || it.estilo) && (
                                <span className="text-zinc-500"> — {[it.color, it.tamano, it.estilo].filter(Boolean).join(" · ")}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {o.envio_precio ? formatMoney(o.envio_precio) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold">{formatMoney(o.total)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {o.anticipo ? (
                        <>
                          <div className="text-zinc-700">{formatMoney(o.anticipo)}</div>
                          {o.anticipo_metodo && (
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                              {o.anticipo_metodo}{o.anticipo_autorizacion ? ` · ${o.anticipo_autorizacion}` : ""}
                            </div>
                          )}
                        </>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      <div className="font-semibold text-orange-700">{formatMoney(aCobrar)}</div>
                    </td>
                    <td className="px-3 py-3">
                      <InlineStatus
                        orderId={o.id}
                        campo="estado_entrega"
                        value={o.estado_entrega || "pendiente"}
                        options={ENTREGA}
                        canEdit
                        onChanged={load}
                      />
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Link to={`/pedidos/${o.id}`} onClick={() => { try { sessionStorage.setItem("pedidos_return_path", "/entrega"); } catch {} }} className="text-[#002FA7] text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-1">
                        Ver <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
